<template>
  <form class="form" @sumbit.prevent>
    <div class="form__content">
      <IdentityQueueStepCounter />
      <h1 class="extra-large-headline">
        {{ $t("PageTitle.headline.pepSofPageHeader") }}
      </h1>

      <div class="question">
        <h3 class="headline">
          {{ $t("PageSubTitle.paragraph.whatIsSofQuestion") }}
        </h3>
        <p class="bodycopy">
          {{ $t("PageSubTitle.paragraph.whatIsSofAnswer") }}
        </p>
      </div>
      <div class="question">
        <h3 class="headline">
          {{ $t("PageSubTitle.paragraph.whyAmIReceivingQuestion") }}
        </h3>
        <p class="bodycopy">
          {{ $t("PageSubTitle.paragraph.whyAmIReceivingAnswer") }}
        </p>
      </div>
      <div class="question">
        <h3 class="headline">
          {{ $t("PageSubTitle.paragraph.whatIsAPepQuestion") }}
        </h3>
        <p class="bodycopy">
          {{ $t("PageSubTitle.paragraph.whatIsAPepAnswer") }}
        </p>
      </div>
    </div>
    <form @submit.prevent>
      <AutocompleteSelect
        v-model:autocomplete="form.country"
        :label="$t('Autocomplete.label.countryResidence')"
        :placeholder="$t('MobileAutocompleteSelect.placeholder.phoneCountry')"
        :type="'text'"
        :items="form.countryList"
      />

      <section>
        <p class="form__question-aide small-headline">
          {{ $t("PageSubTitle.paragraph.dualCitizenshipQuestion") }}
        </p>

        <div class="form__pep-buttons">
          <ButtonBase
            @onButtonClick="toggleDualCitizenship(true)"
            class="button-outlined"
            :class="{ '--is-selected': dualCitizenship === true }"
          >
            {{ $t("ButtonBase.label.yes") }}
          </ButtonBase>
          <ButtonBase
            @onButtonClick="toggleDualCitizenship(false)"
            class="button-outlined"
            :class="{ '--is-selected': dualCitizenship === false }"
          >
            {{ $t("ButtonBase.label.no") }}
          </ButtonBase>
        </div>

        <FormSelect
          v-show="dualCitizenship"
          type="text"
          name="citizenshipCountries"
          :label="$t('FormSelect.label.citizenshipCountries')"
          :maxItems="2"
          isMultiSelect
          :items="form.countryList"
          @dropdownValueUpdate="updateDropdown"
        />
      </section>

      <section v-show="isADefinedBoolean(dualCitizenship)">
        <p class="form__question-aide small-headline">
          {{ $t("PageSubTitle.paragraph.pepRelatedByEmploymentQuestion") }}
        </p>

        <div class="form__pep-buttons">
          <ButtonBase
            @onButtonClick="togglePepRelatedByEmploymentForm(true)"
            class="button-outlined"
            :class="{ '--is-selected': pepRelatedByEmployment === true }"
          >
            {{ $t("ButtonBase.label.yes") }}
          </ButtonBase>
          <ButtonBase
            @onButtonClick="togglePepRelatedByEmploymentForm(false)"
            class="button-outlined"
            :class="{ '--is-selected': pepRelatedByEmployment === false }"
          >
            {{ $t("ButtonBase.label.no") }}
          </ButtonBase>
        </div>
      </section>

      <!-- pep / family member is a pep - related to employment -->
      <Transition name="fade" mode="out-in">
        <section v-show="pepRelatedByEmployment">
          <p class="form__question-aide">
            {{ $t("PepSourceOfFunds.paragraph.pepRelatedByEmployment") }}
          </p>

          <BaseInput
            name="jobTitle"
            :label="$t('BaseInput.label.jobTitle')"
            :placeholder="$t('BaseInput.placeholder.jobTitle')"
            @inputValueUpdate="updateInput"
          />

          <BaseInput
            name="organization"
            :label="$t('BaseInput.label.organization')"
            :placeholder="$t('BaseInput.placeholder.organization')"
            @inputValueUpdate="updateInput"
          />

          <BaseInput
            name="industryType"
            :label="$t('BaseInput.label.industryType')"
            :placeholder="$t('BaseInput.placeholder.industryType')"
            @inputValueUpdate="updateInput"
          />
        </section>
      </Transition>

      <!-- pep / family member is a pep - related NOT to employment -->
      <Transition name="fade" mode="out-in">
        <section v-show="pepRelatedByEmployment === false">
          <p class="form__question-aide">
            {{ $t("PepSourceOfFunds.paragraph.pepNotRelatedByEmployment") }}
          </p>

          <FormSelect
            type="text"
            name="employmentStatus"
            :label="$t('FormSelect.label.employmentStatus')"
            :items="pepFormAnswers.employmentStatus"
            @dropdownValueUpdate="updateDropdown"
          />

          <BaseInput
            v-if="
              form.employmentStatus === 'Employed' ||
              form.employmentStatus === 'Self-employed'
            "
            name="jobTitle"
            :label="$t('BaseInput.label.jobTitle')"
            :placeholder="$t('BaseInput.placeholder.jobTitle')"
            @inputValueUpdate="updateInput"
          />

          <BaseInput
            name="organization"
            :label="$t('BaseInput.label.organization')"
            :placeholder="$t('BaseInput.placeholder.organization')"
            @inputValueUpdate="updateInput"
          />
        </section>
      </Transition>
      <Transition name="fade" mode="out-in">
        <section v-show="isADefinedBoolean(pepRelatedByEmployment)">
          <FormSelect
            type="text"
            name="industryType"
            :label="$t('FormSelect.label.industryType')"
            :items="pepFormAnswers.industryType"
            @dropdownValueUpdate="updateDropdown"
          />

          <FormSelect
            type="text"
            name="yearlyAmount"
            :label="$t('FormSelect.label.yearlyAmount')"
            :allowSort="false"
            :items="pepFormAnswers.yearlyAmount"
            @dropdownValueUpdate="updateDropdown"
          />

          <FormSelect
            type="text"
            name="potMainPurpose"
            :label="$t('FormSelect.label.potMainPurpose')"
            :items="pepFormAnswers.potMainPurpose"
            @dropdownValueUpdate="updateDropdown"
          />

          <FormSelect
            type="text"
            name="originOfFunds"
            :label="$t('FormSelect.label.originOfFunds')"
            :items="pepFormAnswers.originOfFunds"
            @dropdownValueUpdate="updateDropdown"
          />

          <Checkbox
            name="tnc"
            :text="$t('Checkbox.text.pepSofTnc')"
            v-model:value="form.tnc"
            v-model:tnc="form.tnc"
            :errors="pepSourceOfFundsStore.getErrorByField('tnc')"
          />

          <ButtonBase
            :isDisabled="
              pepSourceOfFundsStore.formHasErrors ||
              globalStore.getIsFormLoading
            "
            :isLoading="globalStore.getIsFormLoading"
            @onButtonClick="handleFormSubmit"
          >
            {{ $t("ButtonBase.label.continue") }}
          </ButtonBase>
          <IdentityQueueNext />
        </section>
      </Transition>
    </form>
  </form>
</template>

<script setup>
import { onMounted, ref } from "vue";
import { useRoute, useRouter } from "vue-router";

import { useGlobalStore } from "@/stores/globalStore";
import { usePepSourceOfFundsFormStore } from "@/stores/forms/pepSourceOfFundsFormStore";
import { useVerificationStore } from "@/stores/forms/verificationFormStore";
import { useIdentityQueueStore } from "@/stores/identityQueueStore";
import { useBuyOrderFormStore } from "@/stores/forms/buyOrderFormStore";

import useApiHelpers from "@/composables/api/helpers";
import useCustomerActions from "@/composables/api/useCustomerActions";
import useWatchAndUpdate from "@/composables/helpers/useWatchAndUpdate";
import useGlobalCountriesActions from "@/composables/store/global/useGlobalCountriesActions";

import FormSelect from "@/components/ui/selects/FormSelect.vue";
import Checkbox from "@/components/ui/inputs/Checkbox.vue";
import ButtonBase from "@/components/ui/buttons/ButtonBase.vue";
import AutocompleteSelect from "@/components/ui/selects/AutocompleteSelect.vue";
import BaseInput from "@/components/ui/inputs/BaseInput.vue";
import IdentityQueueStepCounter from "@/components/layout/progress/IdentityQueueStepCounter.vue";
import IdentityQueueNext from "@/components/layout/progress/IdentityQueueNext.vue";

const route = useRoute();
const router = useRouter();

const globalStore = useGlobalStore();
const identityQueueStore = useIdentityQueueStore();
const pepSourceOfFundsStore = usePepSourceOfFundsFormStore();
const verificationStore = useVerificationStore();
const { postRequest } = useApiHelpers();
const { fetchCustomerId } = useCustomerActions();
const { watchAndUpdateStore } = useWatchAndUpdate();
const { getRestrictedCountriesByActiveCoin } = useBuyOrderFormStore();
const { fetchCountries } = useGlobalCountriesActions();

// todo mocked for now, to get from an endpoint
const pepFormAnswers = ref({
  yearlyAmount: [
    "Up to 15,000",
    "15,000 to 30,000",
    "30,000 to 50,000",
    "50,000 to 70,000",
    "70,000 to 100,000",
    "100,000 and more",
  ],
  employmentStatus: [
    "Self-employed",
    "Employed",
    "Retired",
    "Unemployed",
    "Full time students",
  ],
  potMainPurpose: [
    "Investment",
    "Trading",
    "Payment for online purchases",
    "Payment for services",
    "Gaming",
    "Other",
    "Ransomware / virus/ cyber extortion demands",
  ],
  industryType: [
    "Agriculture, Food and Natural Resources",
    "Construction",
    "Architecture",
    "Arts, Audio/Video Technology and Communications",
    "Business Management and Adminstration",
    "Education and Training",
    "Finance",
    "Government and Public Adminstration",
    "Health and Science (eg. medical personnel",
    "Hospitality and Tourism",
    "Human Services (eg. social workers)",
    "Information Technology",
    "Law, Public Safety, Connections and Security",
    "Manufacturing",
    "Marketing, Sales and Service",
    "Non-Governmental Organistion/Charity",
    "Professional Service Provider/Consulting",
    "Real Estate",
    "Science, Technology, Engineering and Mathematics",
  ],
  originOfFunds: [
    "Personal Savings",
    "Salary",
    "Business Income/Profits",
    "Pension",
    "Givernment Benefits",
    "Income from Cryptocurrency Trading",
    "Income from Investments (eg. shares, bonds, investment funds)",
    "Income from sale of real estate or other property",
    "Income from renting/leasing",
    "Private loan from family members / friends / others",
    "Loan from bank",
    "Inheritance",
    "Gift",
    "Other",
  ],
});

const form = ref({
  countryList: [],
  country: pepSourceOfFundsStore.form.country.value,
  citizenshipCountries: pepSourceOfFundsStore.form.citizenshipCountries.value,
  employmentStatus: pepSourceOfFundsStore.form.employmentStatus.value,
  industryType: pepSourceOfFundsStore.form.industryType.value,
  jobTitle: pepSourceOfFundsStore.form.jobTitle.value,
  organization: pepSourceOfFundsStore.form.organization.value,
  yearlyAmount: pepSourceOfFundsStore.form.yearlyAmount.value,
  potMainPurpose: pepSourceOfFundsStore.form.potMainPurpose.value,
  originOfFunds: pepSourceOfFundsStore.form.originOfFunds.value,
  tnc: pepSourceOfFundsStore.form.tnc.value || false,
});
const dualCitizenship = ref(null);
const pepRelatedByEmployment = ref(null);

const updateDropdown = (dropdownData) => {
  const field = Object.keys(dropdownData)[0];
  const value = Object.values(dropdownData)[0];
  form.value[field] = value;
};

const handleFormSubmit = async () => {
  const custId = await fetchCustomerId();
  const countryCode = globalStore.getCountryCode(form.value.country);

  const formData = {
    documentType: "pep",
    documentReference: custId,
    declaredPepSourceOfFunds: {
      country_of_residence: countryCode,
      countries_of_citizenship: form.value.citizenshipCountries,
      employment_status: form.value.employmentStatus,
      employment_industry: form.value.industryType,
      job_title: form.value.jobTitle,
      organization: form.value.organization,
      expected_amount: form.value.yearlyAmount,
      purpose_of_transaction: [form.value.potMainPurpose],
      origin_of_funds: [form.value.originOfFunds],
    },
  };

  globalStore.setIsFormLoading(true);
  postRequest("documents/edd", formData, () => {
    identityQueueStore.updateToNextRoute();
    identityQueueStore.fetchNextIdentityRoute(route).then((nextRoute) => {
      router.push(nextRoute);
    });
  });
};

const updateInput = (data) => {
  const field = Object.keys(data)[0];
  const value = Object.values(data)[0];
  form.value[field] = value;
};

const togglePepRelatedByEmploymentForm = (isPepRelatedByEmployment) => {
  pepRelatedByEmployment.value = isPepRelatedByEmployment;
};

const toggleDualCitizenship = (isDualCitizenShip) => {
  dualCitizenship.value = isDualCitizenShip;
};

const isADefinedBoolean = (variableToCheck) => {
  return variableToCheck === true || variableToCheck === false;
};

onMounted(async () => {
  const formattedCountries = await fetchCountries();
  form.value.countryList = formattedCountries;
});
watchAndUpdateStore(form, pepSourceOfFundsStore, () => {});
</script>

<style lang="scss" scoped>
.question {
  &__title {
    font-size: 1rem;
  }
}

.form {
  &__question-aide {
    padding-top: $padding-sml;
    padding-bottom: $padding-sml;
  }

  &__pep-buttons {
    display: flex;
    flex-direction: row;
    margin-bottom: $padding-sml;
  }

  .button-outlined {
    margin-inline: calc($padding-sml/2);
    background: transparent;
    border: 1px solid var(--primary);

    &.--is-selected {
      background: var(--primary);
    }

    &:hover {
      background: var(--primary);
    }
  }
}
</style>
